/**
 * Sanitize a URL string from untrusted input (event tags, kind 0 metadata,
 * etc.) before using it as an `href`, `src`, CSS `url()`, etc.
 *
 * Returns the canonical href for valid `https:` URLs. Returns `undefined`
 * for anything else — `javascript:`, `data:`, `http:`, relative paths,
 * malformed strings — so the caller can fall back to a placeholder.
 *
 * Why this strict?
 * - `javascript:` would be an XSS in `href`. (Modern browsers also block
 *   it in `<img src>` but defense-in-depth.)
 * - `data:` URIs in `<img>` can be huge (DoS) and bypass image-host
 *   reputation systems used to spot phishing.
 * - `http:` leaks the user's IP to a server controlled by anyone with a
 *   Nostr account, and triggers mixed-content warnings.
 * - Relative paths can trigger same-origin requests we didn't intend.
 */
export function sanitizeUrl(value: string | undefined | null): string | undefined {
  if (!value || typeof value !== 'string') return undefined;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' ? url.href : undefined;
  } catch {
    return undefined;
  }
}
